import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable, signal } from '@angular/core'
import { AbstractControl, FormGroup } from '@angular/forms'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { convertStringToCamelCase, convertStringToSnakeCase } from '@app/v2/shared/helpers/convert-string-case'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { ThemeService } from '@app/v2/shared/services/theme.service'
import { PaginatedResponse, Response } from '@app/v2/shared/types/generic-response.type'
import { InviteTeamMemberPayload, OrganizationUser, User, USER_ROLES } from '@app/v2/shared/types/user.type'
import { environment } from '@environments/environment'
import { catchError, map, Observable, of, throwError } from 'rxjs'
import { TeamMemberApiAction } from '../components/manage-team/constants/team-member-action'
import {
  OrganizationAutoSettlement,
  OrganizationAutoSettlementResponse,
  OrganizationBilling,
  OrganizationBillingResponse,
  OrganizationBillingTransaction,
  OrganizationBillingTransactionCategoriesResponse,
  OrganizationBillingTransactionCategoryApi,
  OrganizationBillingTransactionsPage,
  OrganizationCustomerPortal,
  OrganizationCustomerPortalResponse,
  OrganizationDeletePaymentMethodResponse,
  OrganizationPaymentMethod,
  OrganizationPaymentMethodSetupIntent,
  OrganizationPaymentMethodSetupIntentResponse,
  OrganizationPaymentMethodsResponse,
  OrganizationSetDefaultPaymentMethodResponse,
  OrganizationSettings,
  OrganizationWalletTopUp,
  OrganizationWalletTopUpPaymentIntentRequest,
  OrganizationWalletTopUpRequest,
  OrganizationWalletTopUpResponse,
  OrganizationWalletTopUpResult,
  UpdateOrganizationLogoPayload,
  ValidateInvitedTeamMemberEmailErrors,
  ValidateInvitedTeamMemberEmailResult
} from '../types/organization-settings.type'

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  organizationNameWhileEditing = signal<string | null>(null)

  constructor(private _http: HttpClient, private _themeService: ThemeService) {}

  /**
   * Get organization settings
   * @param {string | number} organizationId Organization ID
   * @returns {Observable<OrganizationSettings>}
   */
  getOrganizationSettings(organizationId: string | number): Observable<OrganizationSettings> {
    return this._http.get<OrganizationSettings>(`${environment.v2.baseUrl}plan/contactless/workspaces/organization-settings?workspace_id=${organizationId}`)
  }

  /**
   * Get organization billing details (wallet balance, plan, Stripe customer id).
   * Uses BypassErrorHandling so a 403 can be handled in the billing UI (no-access empty state)
   * instead of the global access-denied redirect.
   * @param {string | number} organizationId Organization ID
   * @returns {Observable<OrganizationBilling>}
   */
  getOrganizationBilling(organizationId: string | number): Observable<OrganizationBilling> {
    return this._http
      .get<OrganizationBillingResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing`, {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      })
      .pipe(map((response) => response.data))
  }

  /**
   * Get saved payment methods for the organization.
   * @param {string | number} organizationId Organization ID
   * @returns {Observable<OrganizationPaymentMethod[]>}
   */
  getOrganizationPaymentMethods(organizationId: string | number): Observable<OrganizationPaymentMethod[]> {
    return this._http
      .get<OrganizationPaymentMethodsResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/payment-methods`, {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      })
      .pipe(
        map((response) => {
          const rawMethods = response.data?.payment_methods ?? (response.data as { paymentMethods?: unknown[] })?.paymentMethods ?? []
          return (Array.isArray(rawMethods) ? rawMethods : []).map((method) => this._normalizePaymentMethod(method))
        })
      )
  }

  /**
   * Create a Stripe customer portal session for the organization.
   * @param {string | number} organizationId Organization ID
   * @param {string} returnUrl URL Stripe redirects to after the portal session
   * @returns {Observable<OrganizationCustomerPortal>}
   */
  createOrganizationCustomerPortal(organizationId: string | number, returnUrl: string): Observable<OrganizationCustomerPortal> {
    return this._http
      .post<OrganizationCustomerPortalResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/customer-portal`, {
        return_url: returnUrl
      })
      .pipe(map((response) => response.data))
  }

  /**
   * Create a Stripe setup intent for adding a payment method to the organization.
   * @param {string | number} organizationId Organization ID
   * @returns {Observable<OrganizationPaymentMethodSetupIntent>}
   */
  createPaymentMethodSetupIntent(organizationId: string | number): Observable<OrganizationPaymentMethodSetupIntent> {
    return this._http
      .post<OrganizationPaymentMethodSetupIntentResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/payment-methods/setup-intent`, {})
      .pipe(map((response) => response.data))
  }

  /**
   * Set a payment method as the default for the organization.
   * @param {string | number} organizationId Organization ID
   * @param {string} paymentMethodId Stripe payment method ID
   * @returns {Observable<OrganizationSetDefaultPaymentMethodResponse>}
   */
  setDefaultPaymentMethod(organizationId: string | number, paymentMethodId: string): Observable<OrganizationSetDefaultPaymentMethodResponse> {
    return this._http.patch<OrganizationSetDefaultPaymentMethodResponse>(
      `${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/payment-methods/${paymentMethodId}/default`,
      {}
    )
  }

  /**
   * Delete a saved payment method from the organization.
   * @param {string | number} organizationId Organization ID
   * @param {string} paymentMethodId Stripe payment method ID
   * @returns {Observable<OrganizationDeletePaymentMethodResponse>}
   */
  deleteOrganizationPaymentMethod(organizationId: string | number, paymentMethodId: string): Observable<OrganizationDeletePaymentMethodResponse> {
    return this._http.delete<OrganizationDeletePaymentMethodResponse>(
      `${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/payment-methods/${paymentMethodId}`
    )
  }

  /**
   * Enable or disable automatic registration fee settlement from the organization wallet.
   * @param {string | number} organizationId Organization ID
   * @param {boolean} enabled Whether auto-settlement should be enabled
   * @returns {Observable<OrganizationAutoSettlement>}
   */
  updateOrganizationAutoSettlement(organizationId: string | number, enabled: boolean): Observable<OrganizationAutoSettlement> {
    return this._http
      .patch<OrganizationAutoSettlementResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/auto-settlement`, {
        enabled
      })
      .pipe(map((response) => response.data))
  }

  /**
   * Top up the organization wallet with a saved payment method.
   * @param {string | number} organizationId Organization ID
   * @param {OrganizationWalletTopUpRequest} payload Top-up amount and saved payment method ID
   * @returns {Observable<OrganizationWalletTopUpResult>}
   */
  topUpOrganizationWallet(organizationId: string | number, payload: OrganizationWalletTopUpRequest): Observable<OrganizationWalletTopUpResult> {
    return this._http
      .post<OrganizationWalletTopUpResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/top-up`, payload)
      .pipe(map((response) => response.data))
  }

  /**
   * Create a Stripe PaymentIntent for topping up with a newly entered card.
   * Confirmation must be completed client-side via Stripe.js using the returned `client_secret`.
   * @param {string | number} organizationId Organization ID
   * @param {OrganizationWalletTopUpPaymentIntentRequest} payload Top-up amount
   * @returns {Observable<OrganizationWalletTopUpResult>}
   */
  createOrganizationWalletTopUpPaymentIntent(organizationId: string | number, payload: OrganizationWalletTopUpPaymentIntentRequest): Observable<OrganizationWalletTopUpResult> {
    return this._http
      .post<OrganizationWalletTopUpResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/top-up/payment-intent`, payload)
      .pipe(map((response) => response.data))
  }

  /**
   * Get a paginated page of organization wallet transactions.
   * Uses bypass flags so a 403 can be handled silently in the billing UI.
   * @param {string | number} organizationId Organization ID
   * @param {Object} [options] Pagination options
   * @param {number} [options.perPage=20] Number of transactions per page
   * @param {string} [options.startingAfter] Cursor for the next page (`next_starting_after` from the previous response)
   * @returns {Observable<OrganizationBillingTransactionsPage>}
   */
  getOrganizationBillingTransactions(organizationId: string | number, options: { perPage?: number; startingAfter?: string } = {}): Observable<OrganizationBillingTransactionsPage> {
    const perPage = options.perPage ?? 20
    let params = new HttpParams().set('per_page', String(perPage))

    if (options.startingAfter) {
      params = params.set('starting_after', options.startingAfter)
    }

    return this._http
      .get<OrganizationBillingTransactionCategoriesResponse>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/billing/transaction-categories`, {
        params,
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      })
      .pipe(map((response) => this._normalizeBillingTransactionsPage(response, perPage)))
  }

  /**
   * Update organization settings
   * @param {string} organizationId Organization ID
   * @param {Partial<OrganizationSettings>} data Organization settings
   * @returns {Observable<OrganizationSettings>}
   */
  updateOrganizationSettings(data: Partial<OrganizationSettings>): Observable<OrganizationSettings> {
    return this._http.patch<OrganizationSettings>(`${environment.v2.baseUrl}plan/contactless/workspaces/organization-settings`, data, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Update Micetribe admin settings (plan, payment gateway, expiry date)
   * @param {Workspace} workspace Workspace data
   * @returns {Observable<Workspace>}
   */
  updateMicetribeAdminSettings(workspace: Workspace): Observable<Workspace> {
    return this._http.patch<Workspace>(
      `${environment.planningApi}workspaces`,
      {
        ...workspace
      },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }
  /**
   * Get organization users
   * @param {string | number} organizationId Organization ID
   * @param {number} page Page number
   * @param {number} per_page Per page
   * @returns {Observable<PaginatedResponse<OrganizationUser>>}
   */
  getOrganizationUsers(organizationId: string | number, page: number = 1, per_page: number = 10): Observable<PaginatedResponse<OrganizationUser>> {
    return this._http.get<PaginatedResponse<OrganizationUser>>(
      `${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/users?page=${page}&per_page=${per_page}`,
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /**
   * Validate invited team member email
   * @param {string | number} organizationId Organization ID
   * @param {string} email Email to validate
   * @returns {Observable<ValidateInvitedTeamMemberEmailResult>}
   */
  validateInvitedTeamMemberEmail(organizationId: string | number, email: string): Observable<ValidateInvitedTeamMemberEmailResult> {
    return this._http
      .post<ValidateInvitedTeamMemberEmailResult>(
        `${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/users/validate-email`,
        { email },
        {
          context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
        }
      )
      .pipe(
        map(() => ({ valid: true as const })),
        catchError((error: HttpErrorResponse) => {
          const validationErrors = this._parseValidateInvitedTeamMemberEmailErrors(error.error)
          if (error.status === 422 && validationErrors) {
            return of({ valid: false as const, errors: validationErrors })
          }
          return throwError(() => error)
        })
      )
  }

  /**
   * Invite team member
   * @param {number} organizationId Organization ID
   * @param {InviteTeamMemberPayload} payload Invite team member payload
   * @returns {Observable<Response<InviteTeamMemberPayload>>}
   */
  inviteTeamMember(organizationId: number, payload: InviteTeamMemberPayload): Observable<Response<InviteTeamMemberPayload>> {
    return this._http.post<Response<InviteTeamMemberPayload>>(`${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/users/invite-team`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Perform a team member action (suspend, remove, activate, etc.)
   * @param {number} organizationId Organization ID
   * @param {number} id Team member invite ID
   * @param {TeamMemberApiAction} action API action segment (e.g. suspend, remove, activate)
   * @param {boolean} [notify] Whether to notify the user by email (suspend/remove)
   * @returns {Observable<Response<void>>}
   */
  performTeamMemberAction(
    organizationId: number,
    id: number,
    action: TeamMemberApiAction,
    notify?: boolean
  ): Observable<Response<{ data: []; message: string; success: boolean }>> {
    return this._http.post<Response<{ data: []; message: string; success: boolean }>>(
      `${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/users/${id}/${action}`,
      notify === undefined ? {} : { notify },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /**
   * Update team member role
   * @param {number} organizationId Organization ID
   * @param {number} id Team member invite ID
   * @param {USER_ROLES} role Team member role
   * @returns {Observable<Response<void>>}
   */
  updateTeamMemberRole(organizationId: number, id: number, role: USER_ROLES): Observable<Response<void>> {
    return this._http.patch<Response<void>>(
      `${environment.v2.baseUrl}plan/contactless/organizations/${organizationId}/users/${id}/update-role`,
      { role },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /**
   * Get update payload from form controls
   * @param {FormGroup} form Form group
   * @returns {Partial<OrganizationSettings>} Update payload
   */
  getUpdatePayload(form: FormGroup): Partial<OrganizationSettings> {
    const payload: Partial<OrganizationSettings> = {}

    if (!form) return payload

    Object.keys(form.controls).forEach((controlName) => {
      const control = form.get(controlName)
      if (control?.dirty) {
        // Include dirty controls that are valid
        if (control.valid) {
          payload[convertStringToSnakeCase(controlName)] = control.value
        }
        // Include dirty controls that only have server errors (user might have fixed the issue)
        else if (control.errors) {
          const hasOnlyServerError = Object.keys(control.errors).length === 1 && control.errors['serverError']
          const hasServerErrorWithClientErrors = control.errors['serverError'] && Object.keys(control.errors).length > 1

          // Only include if it has ONLY server error (no client validation errors)
          // AND the value has actually changed since the server error was applied
          if (hasOnlyServerError) {
            const serverErrorValue = control['_serverErrorValue']
            const valueHasChanged = serverErrorValue !== control.value

            // Only include if the value has changed since the server error
            if (valueHasChanged) {
              payload[convertStringToSnakeCase(controlName)] = control.value
              // Update the stored server error value to prevent duplicate requests
              control['_serverErrorValue'] = control.value
            }
          }
        }
      }
    })

    return payload
  }

  /**
   * Check if form has unsaved changes
   * @param {FormGroup} form Form group
   * @returns {boolean} True if form is dirty
   */
  isFormDirty(form: FormGroup): boolean {
    return !!form?.dirty
  }

  /**
   * Check if form is valid
   * @param {FormGroup} form Form group
   * @returns {boolean} True if form is valid
   */
  isFormValid(form: FormGroup): boolean {
    return !!form?.valid
  }

  /**
   * Mark form as pristine after successful update
   * @param {FormGroup} form Form group
   */
  markFormPristine(form: FormGroup): void {
    form?.markAsPristine()
  }

  /**
   * Handle server validation errors - clear existing ones and optionally apply new ones
   * @param {FormGroup} form Form group
   * @param {any} serverErrors Server validation errors to apply (optional - if null, only clears)
   * @param {boolean} persistent Whether to make server errors persistent during typing
   */
  handleServerValidationErrors(form: FormGroup, serverErrors?: any, persistent: boolean = true): void {
    if (!form) return

    // First, clear any existing server errors and restore validators
    Object.keys(form.controls).forEach((controlName) => {
      const control = form.get(controlName)
      if (control && control.errors && control.errors['serverError']) {
        const errors = { ...control.errors }
        delete errors['serverError']
        control.setErrors(Object.keys(errors).length > 0 ? errors : null)

        // Restore original validators if they were modified
        if (control['_originalValidators']) {
          control.setValidators(control['_originalValidators'])
          control.setAsyncValidators(control['_originalAsyncValidators'])
          delete control['_originalValidators']
          delete control['_originalAsyncValidators']
          control.updateValueAndValidity()
        }

        // Clean up stored server error value
        if (control['_serverErrorValue'] !== undefined) {
          delete control['_serverErrorValue']
        }
      }
    })

    // If no new errors to apply, we're done
    if (!serverErrors) return

    // Apply new server errors
    Object.keys(serverErrors).forEach((fieldName) => {
      const controlName = this._convertToCamelCaseWithSpecialHandling(fieldName)
      const control = form.get(controlName)

      if (control && serverErrors[fieldName] && Array.isArray(serverErrors[fieldName])) {
        const errorMessage = serverErrors[fieldName][0]

        if (persistent) {
          // Store original validators and create persistent validator
          const originalValidators = control.validator
          const originalAsyncValidators = control.asyncValidator

          control['_originalValidators'] = originalValidators
          control['_originalAsyncValidators'] = originalAsyncValidators
          control['_serverErrorValue'] = control.value

          const serverErrorPreservingValidator = (control: AbstractControl) => {
            const errors = originalValidators ? originalValidators(control) : null
            return errors ? { ...errors, serverError: errorMessage } : { serverError: errorMessage }
          }

          const wasDirty = control.dirty
          control.setErrors({ ...control.errors, serverError: errorMessage })
          control.markAsTouched()
          control.setValidators(serverErrorPreservingValidator)
          control.setAsyncValidators(originalAsyncValidators)
          control.updateValueAndValidity()

          if (wasDirty) control.markAsDirty()
        } else {
          // Simple server error application
          control.setErrors({ ...control.errors, serverError: errorMessage })
          control.markAsTouched()
        }
      }
    })
  }

  /**
   * @description Gets skeleton theme colors based on current theme
   * @returns {object} Skeleton theme colors
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    const isDark = this._themeService.theme() === 'dark'
    return {
      backgroundColor: isDark ? '#374151' : '#E5E7EB',
      color: isDark ? '#4B5563' : '#D1D5DB'
    }
  }

  /**
   * @description Gets the workspace paid events
   * @param {string} workspace_id Workspace ID
   * @returns {Observable<ServerRepresentedEvent[]>}
   */
  public getWorkspacePaidEvents(workspace_id: string): Observable<ServerRepresentedEvent[]> {
    return this._http.get<{ message: string; data: Partial<ServerRepresentedEvent>[] }>(`${environment.planningApi}events/paid`, { params: { workspace_id } }).pipe(
      map((response: { message: string; data: Partial<ServerRepresentedEvent>[] }) => {
        return response.data as ServerRepresentedEvent[]
      })
    )
  }

  /**
   * Convert string to camelCase with special handling for organization fields
   * @param {string} value Field name to convert
   * @returns {string} Camel case field name
   */
  private _convertToCamelCaseWithSpecialHandling(value: string): string {
    // Handle special mappings for Micetribe admin fields
    switch (value) {
      case 'options.payments.gateway':
        return 'paymentGateway'
      case 'plan_expiry_date':
        return 'planExpiryDate'
      case 'plan_id':
        return 'pro' // pro checkbox controls plan_id
      default:
        // Use standard conversion for normal cases
        return convertStringToCamelCase(value)
    }
  }

  /**
   * Parse validate invited team member email errors from a flat or nested 422 body.
   * @param {unknown} value Value to parse
   * @returns {ValidateInvitedTeamMemberEmailErrors | null} Parsed email errors, or null
   */
  private _parseValidateInvitedTeamMemberEmailErrors(value: unknown): ValidateInvitedTeamMemberEmailErrors | null {
    if (this._isValidateInvitedTeamMemberEmailErrors(value)) return value

    if (value && typeof value === 'object' && 'errors' in value) {
      const nestedErrors = (value as { errors: unknown }).errors
      if (this._isValidateInvitedTeamMemberEmailErrors(nestedErrors)) return nestedErrors
    }

    return null
  }

  /**
   * Check if value is validate invited team member email errors
   * @param {unknown} value Value to check
   * @returns {boolean} True if value is validate invited team member email errors
   */
  private _isValidateInvitedTeamMemberEmailErrors(value: unknown): value is ValidateInvitedTeamMemberEmailErrors {
    return !!value && typeof value === 'object' && Array.isArray((value as ValidateInvitedTeamMemberEmailErrors).email)
  }

  /**
   * @description Updates the organization logo
   * @param {UpdateOrganizationLogoPayload} payload Payload
   * @returns {Observable<Response>}
   */
  public updateOrganizationLogo(payload: UpdateOrganizationLogoPayload): Observable<Response<void>> {
    return this._http.patch<Response<void>>(`${environment.v2.baseUrl}plan/contactless/workspaces`, payload)
  }

  /**
   * Normalize payment method payloads from the billing API into a consistent shape.
   * @param {unknown} method Raw payment method object from the API
   * @returns {OrganizationPaymentMethod} Normalized payment method
   */
  private _normalizePaymentMethod(method: unknown): OrganizationPaymentMethod {
    const raw = (method ?? {}) as Record<string, unknown>

    return {
      id: String(raw['id'] ?? ''),
      brand: String(raw['brand'] ?? ''),
      last4: String(raw['last4'] ?? ''),
      exp_month: Number(raw['exp_month'] ?? raw['expMonth'] ?? 0),
      exp_year: Number(raw['exp_year'] ?? raw['expYear'] ?? 0),
      is_default: Boolean(raw['is_default'] ?? raw['isDefault'] ?? raw['default'] ?? false)
    }
  }

  /**
   * Unwrap transaction-categories API payloads that may be nested under `data`.
   * @param {OrganizationBillingTransactionCategoriesResponse} response Raw API response
   * @returns {OrganizationBillingTransactionCategoriesResponse} Payload containing categories and pagination meta
   */
  private _unwrapTransactionCategoriesResponse(response: OrganizationBillingTransactionCategoriesResponse): OrganizationBillingTransactionCategoriesResponse {
    if (response.data) {
      return {
        transaction_categories: response.data.transaction_categories ?? response.transaction_categories,
        meta: response.data.meta ?? response.meta
      }
    }

    return response
  }

  /**
   * Extract transaction rows from the transaction-categories billing API payload.
   * @param {OrganizationBillingTransactionCategoriesResponse} response Raw API response
   * @returns {OrganizationBillingTransactionCategoryApi[]} Transaction rows
   */
  private _extractBillingTransactions(response: OrganizationBillingTransactionCategoriesResponse): OrganizationBillingTransactionCategoryApi[] {
    const payload = this._unwrapTransactionCategoriesResponse(response)
    return Array.isArray(payload.transaction_categories) ? payload.transaction_categories : []
  }

  /**
   * Map a transaction-categories API row into the UI transaction shape.
   * @param {OrganizationBillingTransactionCategoryApi} item Raw transaction object from the API
   * @returns {OrganizationBillingTransaction} Normalized transaction row
   */
  private _normalizeBillingTransaction(item: OrganizationBillingTransactionCategoryApi): OrganizationBillingTransaction {
    const total = Number(item.total ?? 0)
    const transactionDate = String(item.transaction_date ?? '').trim()
    const transactionTime = String(item.transaction_time ?? '').trim()
    const date = transactionDate && transactionTime ? `${transactionDate}, ${transactionTime}` : transactionDate || transactionTime
    const receiptUrl = item.receipt_url

    return {
      id: String(item.id ?? ''),
      debit: total < 0 ? Math.abs(total) : 0,
      credit: total > 0 ? total : 0,
      description: String(item.description ?? item.title ?? item.label ?? ''),
      date,
      url: receiptUrl ? String(receiptUrl) : undefined,
      balance: Number(item.balance ?? 0)
    }
  }

  /**
   * Normalize a transaction-categories billing API page into UI-ready transactions.
   * @param {OrganizationBillingTransactionCategoriesResponse} response Raw API response
   * @param {number} perPage Requested page size
   * @returns {OrganizationBillingTransactionsPage} Normalized transactions page
   */
  private _normalizeBillingTransactionsPage(response: OrganizationBillingTransactionCategoriesResponse, perPage: number): OrganizationBillingTransactionsPage {
    const payload = this._unwrapTransactionCategoriesResponse(response)
    const pagination = payload.meta?.pagination
    const transactions = this._extractBillingTransactions(response).map((transaction) => this._normalizeBillingTransaction(transaction))
    const nextStartingAfter = pagination?.next_starting_after ? String(pagination.next_starting_after) : null
    const apiHasMore = pagination?.has_more ?? transactions.length >= perPage

    return {
      transactions,
      has_more: Boolean(nextStartingAfter && apiHasMore),
      next_starting_after: nextStartingAfter
    }
  }
}
